import { useState } from 'react';
import { supabase, handleSupabaseError, retryOperation } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { PostgrestError } from '@supabase/supabase-js';

export function useGarden() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addToGarden = async (plantId: string) => {
    if (!user) {
      throw new Error('Must be logged in to add plants to garden');
    }

    setLoading(true);
    setError(null);

    let retryCount = 0;
    const maxRetries = 3;

    const tryAddToGarden = async (): Promise<void> => {
      try {
        await retryOperation(async () => {
          // Check if plant exists
          const { data: plant, error: plantError } = await supabase
            .from('plants')
            .select('id')
            .eq('id', plantId)
            .maybeSingle();

          if (plantError) throw plantError;
          if (!plant) throw new Error('Plant not found');

          // Check if plant already exists in user's garden
          const { data: existingPlants, error: existingError } = await supabase
            .from('user_gardens')
            .select('id')
            .eq('user_id', user.id)
            .eq('plant_id', plantId);

          if (existingError) throw existingError;
          if (existingPlants?.length > 0) {
            throw new Error('This plant is already in your garden');
          }

          // Add plant to user's garden
          const { error: insertError } = await supabase
            .from('user_gardens')
            .insert({
              user_id: user.id,
              plant_id: plantId,
              planting_date: new Date().toISOString()
            });

          if (insertError) throw insertError;
        });
      } catch (err) {
        // Handle Supabase-specific errors
        if ((err as PostgrestError)?.code === 'PGRST116') {
          return; // Not found, but not an error
        }

        if (retryCount < maxRetries) {
          retryCount++;
          // Exponential backoff
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return tryAddToGarden();
        }
        
        const message = handleSupabaseError(err);
        setError(message);
        throw new Error(message);
      }
    };

    try {
      await tryAddToGarden();
    } finally {
      setLoading(false);
    }
  };

  return {
    addToGarden,
    loading,
    error
  };
}